import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import { Camera, Loader2, X as XIcon, Sparkles } from "lucide-react";
import { PrescriptionSelector, PRESCRIPTION_PRICING } from "./PrescriptionSelector";
import { LogisticsFaq } from "./LogisticsFaq";
import { DisclaimerModal } from "./DisclaimerModal";
import { CrisisBanner, detectCrisis } from "./CrisisBanner";
import { renderWithMobileBreaks } from "../utils/mobileBreaks";

// Session id shared with FAQ instrumentation (created on first mount).
const getSessionId = () => {
  try {
    let s = sessionStorage.getItem("mp-session-id");
    if (!s) {
      s = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem("mp-session-id", s);
    }
    return s;
  } catch {
    return "s_anon";
  }
};

// Loose email shape check — server validates strictly.
const looksLikeEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s || "");

const CURRENCY_FORMAT = {
  en: (a) => `$${a.toFixed(0)} USD`,
  "zh-HK": (a) => `HK$${a.toFixed(0)}`,
  "zh-TW": (a) => `NT$${a.toFixed(0)}`,
  "zh-CN": (a) => `¥${a.toFixed(0)} RMB`,
};

const formatDeliveryDate = (lang, duration) => {
  const d = new Date();
  d.setDate(d.getDate() + duration);
  if (lang.startsWith("zh")) {
    return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日`;
  }
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

export const CapsuleForm = React.forwardRef(({ onSealed, duration, onDurationChange }, ref) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language in PRESCRIPTION_PRICING ? i18n.language : "en";
  const price = PRESCRIPTION_PRICING[lang][duration];
  const priceFmt = CURRENCY_FORMAT[lang](price);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // Gift-flow email decoupling:
  //   • `email` above is the RECIPIENT — where the sealed memory lands
  //     in 90/180/365 days. Always persisted to `user_email` in the DB.
  //   • `billingSameAsRecipient` (default FALSE — see UX note below) shows
  //     the billing field by default so the payer instantly sees where to
  //     type their own address for the Stripe receipt. Checking the box
  //     collapses to a single-email flow AND auto-copies the recipient
  //     email into the billing state (see the effect below).
  //   • Returning users whose localStorage draft has an explicit boolean
  //     for this preference will have that preference respected via the
  //     restore path, so we don't clobber their previous choice.
  const [billingSameAsRecipient, setBillingSameAsRecipient] = useState(false);
  const [billingEmail, setBillingEmail] = useState("");
  // Auto-copy: whenever the user re-checks "same as recipient", mirror
  // the current recipient email into the billing input so if they later
  // toggle back off, the field is already populated (not empty). Kept as
  // its own effect for clarity and to avoid coupling the checkbox
  // handler to the input handler.
  useEffect(() => {
    if (billingSameAsRecipient) setBillingEmail(email);
  }, [billingSameAsRecipient, email]);
  const [letter, setLetter] = useState("");

  // ─── Draft persistence (localStorage) ────────────────────────────────
  // Never let a user lose 20 minutes of writing because they closed a
  // Stripe tab or lost signal. We continuously autosave the composition
  // to `mp_letter_draft` and restore on mount. Cleared explicitly on
  // /success (payment confirmed). Wrapped in try/catch — private-mode
  // Safari throws on localStorage.setItem, so we degrade silently.
  const DRAFT_KEY = "mp_letter_draft";
  const draftRestoredRef = useRef(false);
  useEffect(() => {
    // Restore ONCE on mount, before any user edit fires the save effect.
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d && typeof d === "object") {
        if (typeof d.letter === "string") setLetter(d.letter);
        if (typeof d.email === "string") setEmail(d.email);
        if (typeof d.name === "string") setName(d.name);
        if (typeof d.billingEmail === "string") setBillingEmail(d.billingEmail);
        if (typeof d.billingSameAsRecipient === "boolean")
          setBillingSameAsRecipient(d.billingSameAsRecipient);
        if (Number.isInteger(d.duration) && [90, 180, 365].includes(d.duration)) {
          // Only propagate if the parent form isn't already showing a
          // fresh duration (avoids clobbering a fresh URL-primed value).
          onDurationChange?.(d.duration);
        }
      }
    } catch { /* private mode / corrupted JSON — ignore */ }
  }, []);
  // Autosave: fires on every meaningful edit. Debounced via a 500ms
  // trailing timer to avoid pounding localStorage on every keystroke.
  useEffect(() => {
    if (!draftRestoredRef.current) return; // don't save before restore
    const handle = setTimeout(() => {
      try {
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({
            letter,
            email,
            name,
            billingSameAsRecipient,
            billingEmail,
            duration,
            savedAt: new Date().toISOString(),
          })
        );
      } catch { /* private mode / quota — silently drop */ }
    }, 500);
    return () => clearTimeout(handle);
  }, [letter, email, name, billingSameAsRecipient, billingEmail, duration]);
  const [photoPath, setPhotoPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Disclaimer consent gate (legal): user must affirmatively check before checkout.
  const [consent, setConsent] = useState(false);
  const [consentAt, setConsentAt] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  // Promo code state — either auto-primed from /vip/:code route (via
  // sessionStorage) or entered manually in the "Have a code?" toggle.
  //
  // Two-track promo model (mirrors backend):
  //   • kind === "slot_capped_free"      → 100%-off via /payments/redeem-code
  //                                        (MIPROSE50 — 50-slot atomic pool)
  //   • kind === "checkout_percent_off"  → % discount applied at Stripe
  //                                        checkout (PH20 — permanent).
  // We keep both kind + discount_percent in state so the UI can render the
  // exact math (strikethrough base + discounted price) rather than
  // hardcoding FREE for every valid code.
  const [promoCode, setPromoCode] = useState(null);
  const [promoRemaining, setPromoRemaining] = useState(null);
  const [promoKind, setPromoKind] = useState(null);
  const [promoDiscountPercent, setPromoDiscountPercent] = useState(0);
  const [promoInputOpen, setPromoInputOpen] = useState(false);
  const [promoDraft, setPromoDraft] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);
  // Crisis pre-screen state — debounced detection + per-session dismiss latch.
  const [crisisDetected, setCrisisDetected] = useState(false);
  const [crisisDismissed, setCrisisDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("mp-crisis-dismissed") === "1";
    } catch {
      return false;
    }
  });
  const fileInputRef = useRef();

  // Debounced real-time scan of the letter for crisis markers.
  useEffect(() => {
    if (crisisDismissed) return;
    const handle = setTimeout(() => {
      setCrisisDetected(detectCrisis(letter));
    }, 700);
    return () => clearTimeout(handle);
  }, [letter, crisisDismissed]);

  const dismissCrisis = () => {
    setCrisisDetected(false);
    setCrisisDismissed(true);
    try {
      sessionStorage.setItem("mp-crisis-dismissed", "1");
    } catch { /* ignore */ }
  };

  // ----- Promo code auto-prime (from /vip/:code route) + validator -----
  const validatePromo = async (rawCode) => {
    const code = String(rawCode || "").trim().toUpperCase();
    if (!code) return;
    setPromoChecking(true);
    try {
      const res = await api.get(`/promo/${encodeURIComponent(code)}/status`);
      if (res.data.valid) {
        setPromoCode(code);
        setPromoRemaining(res.data.remaining);
        setPromoKind(res.data.kind || "slot_capped_free");
        setPromoDiscountPercent(Number(res.data.discount_percent) || 100);
        setPromoInputOpen(false);
        setPromoDraft("");
        setError("");
      } else if (res.data.exhausted) {
        toast.error(t("promo.error_exhausted"));
        setPromoCode(null);
        setPromoRemaining(0);
      } else {
        toast.error(t("promo.error_invalid"));
      }
    } catch {
      toast.error(t("promo.error_invalid"));
    } finally {
      setPromoChecking(false);
    }
  };

  useEffect(() => {
    try {
      const primed = sessionStorage.getItem("mp-promo-code");
      if (primed) {
        validatePromo(primed);
      }
    } catch { /* ignore */ }
    // Run once on mount only; validator is stable enough for our use.
  }, []);

  const clearPromo = () => {
    setPromoCode(null);
    setPromoRemaining(null);
    setPromoKind(null);
    setPromoDiscountPercent(0);
    setPromoInputOpen(false);
    setPromoDraft("");
    try { sessionStorage.removeItem("mp-promo-code"); } catch { /* ignore */ }
  };

  // ----- Abandoned-cart instrumentation -----
  // Debounced server-side draft upsert. PRIVACY: we only send metadata
  // (session id, name, email, length, duration, locale) — NEVER any
  // fragment of the letter body. The recovery email is metadata-only;
  // the user's actual writing lives in their own localStorage.
  const sessionIdRef = useRef(getSessionId());
  const lastSentSnapshotRef = useRef("");
  useEffect(() => {
    const meaningful = letter.trim().length >= 30 && looksLikeEmail(email);
    if (!meaningful) return;
    const snapshot = `${email}|${duration}|${letter.length}`;
    if (snapshot === lastSentSnapshotRef.current) return;
    const handle = setTimeout(() => {
      const payload = {
        session_id: sessionIdRef.current,
        user_name: name.trim() || null,
        user_email: email.trim().toLowerCase(),
        letter_length: letter.length,
        duration_days: duration,
        language_code: lang,
      };
      api.post("/track/draft", payload).then(() => {
        lastSentSnapshotRef.current = snapshot;
      }).catch(() => { /* fire-and-forget */ });
    }, 3000);
    return () => clearTimeout(handle);
  }, [name, email, letter, duration, lang]);

  const handlePhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoPath(res.data.path);
    } catch (e) {
      setError(e.response?.data?.detail || String(e));
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (letter.trim().length < 4) {
      setError(t("form.min_letter"));
      return;
    }
    if (!consent) {
      setError(t("consent.required_error"));
      return;
    }
    // Gift-flow validation: if the user unchecked "same as recipient" we
    // require a valid, DISTINCT billing email. Empty / same-as-recipient
    // both collapse back to the single-email flow.
    const _recipient = email.trim().toLowerCase();
    let _billing = billingSameAsRecipient ? "" : billingEmail.trim().toLowerCase();
    if (_billing && !looksLikeEmail(_billing)) {
      setError(t("form.billing_email_invalid"));
      return;
    }
    if (_billing === _recipient) _billing = ""; // collapse duplicate
    setSubmitting(true);
    try {
      const capRes = await api.post("/capsules", {
        user_name: name.trim(),
        user_email: email.trim(),
        // Only send billing_email when it's genuinely distinct — keeps
        // the DB shape unchanged for the 95% solo-letter case.
        ...(_billing ? { billing_email: _billing } : {}),
        letter_content: letter,
        photo_path: photoPath,
        language_code: lang,
        duration_days: duration,
        disclaimer_accepted_at: consentAt,
      });
      const capsule = capRes.data;

      // ---- Promo path: bypass Stripe entirely (100%-off slot-capped only) ----
      if (promoCode && promoKind === "slot_capped_free") {
        try {
          await api.post("/payments/redeem-code", {
            capsule_id: capsule.id,
            code: promoCode,
          });
          onSealed?.(capsule);
          try { sessionStorage.removeItem("mp-promo-code"); } catch { /* ignore */ }
          navigate(`/success?capsule_id=${capsule.id}&promo=1`);
          return;
        } catch (redeemErr) {
          const status = redeemErr.response?.status;
          if (status === 409) {
            toast.error(t("promo.error_exhausted"));
            clearPromo();
            // Fall through to Stripe checkout below.
          } else if (status === 404) {
            toast.error(t("promo.error_invalid"));
            clearPromo();
          } else {
            setError(redeemErr.response?.data?.detail || String(redeemErr));
            setSubmitting(false);
            return;
          }
        }
      }

      // ---- Standard Stripe checkout ----
      // For percent-off community codes (PH20 family), pass the code so the
      // backend applies the discount multiplier to the Stripe line item.
      const checkoutPayload = {
        capsule_id: capsule.id,
        origin_url: window.location.origin,
      };
      if (promoCode && promoKind === "checkout_percent_off") {
        checkoutPayload.promo_code = promoCode;
      }
      const checkoutRes = await api.post("/payments/checkout", checkoutPayload);
      onSealed?.(capsule);
      window.location.href = checkoutRes.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || String(e));
      setSubmitting(false);
    }
  };

  return (
    <section id="capsule" ref={ref} className="py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[var(--wax)] mb-6">— 02 —</p>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight">{t("form.title")}</h2>
        <p className="mt-3 text-[var(--ink-soft)] italic">{t("form.subtitle")}</p>

        <form onSubmit={onSubmit} className="mt-14 space-y-12" data-testid="capsule-form">
          <div className="flex flex-col gap-3">
            <PrescriptionSelector value={duration} onChange={onDurationChange} />
            {/* Duration reassurance — dismisses "am I writing enough?"
                anxiety before the user even opens the composer. */}
            <p
              className="text-[12px] italic leading-relaxed text-[var(--ink-soft)]"
              data-testid="duration-hint"
            >
              {t("form.duration_hint")}
            </p>
          </div>

          <Field
            id="name-input"
            label={t("form.name_label")}
            value={name}
            onChange={setName}
            placeholder={t("form.name_placeholder")}
            required
            maxLength={80}
          />
          {/* Recipient email + inline hint — strict vertical stack, no
              negative-margin hacks. gap-2 gives an intentional breathing
              gap that never collapses onto the input's focus-line. */}
          <div className="flex flex-col gap-2">
            <Field
              id="email-input"
              label={t("form.recipient_email_label")}
              type="email"
              value={email}
              onChange={setEmail}
              placeholder={t("form.recipient_email_placeholder")}
              required
            />
            <p
              className="text-[12px] italic leading-relaxed text-[var(--ink-soft)]"
              data-testid="recipient-email-hint"
            >
              {t("form.recipient_email_hint")}
            </p>
          </div>

          {/* ─── Billing / sender email decouple (gift-flow) ─────────── */}
          <div className="flex flex-col gap-4" data-testid="billing-email-block">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="billing-same-checkbox"
                checked={billingSameAsRecipient}
                onChange={(e) => setBillingSameAsRecipient(e.target.checked)}
                className="mt-[3px] h-4 w-4 accent-[var(--wax)] cursor-pointer"
              />
              <span className="text-[13px] text-[var(--ink-soft)] leading-relaxed">
                {t("form.billing_same_label")}
              </span>
            </label>

            {!billingSameAsRecipient && (
              <div
                className="flex flex-col gap-2"
                data-testid="billing-email-field-wrap"
              >
                <Field
                  id="billing-email-input"
                  label={t("form.billing_email_label")}
                  type="email"
                  value={billingEmail}
                  onChange={setBillingEmail}
                  placeholder={t("form.billing_email_placeholder")}
                  required
                />
                <p className="text-[12px] italic leading-relaxed text-[var(--ink-soft)]">
                  {t("form.billing_email_hint")}
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-[12px] uppercase tracking-[0.28em] text-[var(--ink-soft)] mb-4">
              {t("form.letter_label")}
            </label>
            <textarea
              data-testid="letter-input"
              value={letter}
              onChange={(e) => setLetter(e.target.value.slice(0, 5000))}
              placeholder={t("form.letter_placeholder")}
              rows={11}
              maxLength={5000}
              className="ruled-paper w-full bg-transparent text-[18px] text-[var(--ink)] resize-none outline-none placeholder:text-[var(--ink-mute)] placeholder:italic"
              required
            />
            {/* Subtle character counter — right-aligned. Turns wax-red as
                the writer approaches the ceiling so they can self-edit
                before the hard clamp trims trailing keystrokes. */}
            <div
              className="mt-2 flex justify-end text-[11px] tracking-[0.22em] tabular-nums text-[var(--ink-mute)]"
              data-testid="letter-char-counter"
              aria-live="polite"
            >
              <span className={letter.length >= 4800 ? "text-[var(--wax)]" : ""}>
                {letter.length.toLocaleString()} / 5,000
              </span>
            </div>
            <CrisisBanner
              visible={crisisDetected && !crisisDismissed}
              onDismiss={dismissCrisis}
            />
          </div>

          <PhotoUpload
            uploading={uploading}
            photoPath={photoPath}
            onFile={handlePhoto}
            onClear={() => setPhotoPath(null)}
            inputRef={fileInputRef}
          />

          <CheckoutSummary
            lang={lang}
            price={price}
            priceFmt={priceFmt}
            deliveryDate={formatDeliveryDate(lang, duration)}
            promoActive={!!promoCode}
            promoKind={promoKind}
            promoDiscountPercent={promoDiscountPercent}
          />

          {/* Promo code: badge when applied · toggle input when not */}
          <PromoBlock
            promoCode={promoCode}
            promoRemaining={promoRemaining}
            promoKind={promoKind}
            promoDiscountPercent={promoDiscountPercent}
            promoInputOpen={promoInputOpen}
            promoDraft={promoDraft}
            promoChecking={promoChecking}
            onOpenInput={() => setPromoInputOpen(true)}
            onDraftChange={setPromoDraft}
            onApply={() => validatePromo(promoDraft)}
            onCloseInput={() => { setPromoInputOpen(false); setPromoDraft(""); }}
            onRemove={clearPromo}
          />

          <LogisticsFaq />

          {error && (
            <div data-testid="form-error" className="text-[var(--wax-deep)] text-sm italic">
              {error}
            </div>
          )}

          {/* Consent gate (disclaimer acceptance) */}
          <label
            data-testid="consent-row"
            className="flex items-start gap-3.5 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              data-testid="consent-checkbox"
              checked={consent}
              onChange={(e) => {
                const ok = e.target.checked;
                setConsent(ok);
                setConsentAt(ok ? new Date().toISOString() : null);
                if (ok) setError("");
              }}
              className="mt-1 h-4 w-4 shrink-0 accent-[var(--wax)] cursor-pointer"
            />
            <span className="text-[12.5px] leading-[1.65] text-[var(--ink-soft)]">
              {t("consent.before_link")}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowDisclaimer(true);
                }}
                data-testid="open-disclaimer-link"
                className="mx-1 text-[var(--wax)] underline underline-offset-4 decoration-[var(--wax)]/60 hover:decoration-[var(--wax)] hover:text-[var(--ink)] transition-colors italic"
              >
                {t("consent.link_text")}
              </button>
              {t("consent.after_link")}
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting || uploading || !consent}
            data-testid="seal-and-pay-btn"
            aria-disabled={!consent}
            className="btn-wax w-full py-5 text-[13px] uppercase tracking-[0.22em] flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin" size={16} /> {t("form.submitting")}
              </>
            ) : promoCode && promoKind === "slot_capped_free" ? (
              t("promo.submit_free", { code: promoCode })
            ) : promoCode && promoKind === "checkout_percent_off" ? (
              t("form.submit", {
                price: CURRENCY_FORMAT[lang](
                  Math.round(price * (1 - (Number(promoDiscountPercent) || 0) / 100) * 100) / 100
                ),
              })
            ) : (
              t("form.submit", { price: priceFmt })
            )}
          </button>

          {/* Payment-methods footnote — WeChat Pay footnote is critical:
              Stripe rejects WeChat Pay for wallets outside Mainland RMB,
              and Stripe's own hosted UI doesn't disclose this. Surfacing
              it BEFORE checkout prevents a whole class of "why did my
              payment fail?" support tickets. */}
          <p
            className="text-center text-[11px] italic leading-relaxed text-[var(--ink-mute)]"
            data-testid="payment-methods-footnote"
          >
            {t("form.payment_methods_footnote")}
          </p>
        </form>
      </div>
      <DisclaimerModal open={showDisclaimer} onClose={() => setShowDisclaimer(false)} />
    </section>
  );
});

const Field = ({ id, label, value, onChange, ...rest }) => (
  <div>
    <label htmlFor={id} className="block text-[12px] uppercase tracking-[0.28em] text-[var(--ink-soft)] mb-3">
      {label}
    </label>
    <input
      id={id}
      data-testid={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="focus-line w-full text-[20px] py-3 text-[var(--ink)] placeholder:text-[var(--ink-mute)] placeholder:italic"
      {...rest}
    />
  </div>
);

const PhotoUpload = ({ uploading, photoPath, onFile, onClear, inputRef }) => {
  const { t } = useTranslation();
  return (
    <div>
      <label className="block text-[12px] uppercase tracking-[0.28em] text-[var(--ink-soft)] mb-5">
        {renderWithMobileBreaks(t("form.photo_label"))}
      </label>
      <input
        type="file"
        accept="image/*"
        ref={inputRef}
        onChange={(e) => onFile(e.target.files?.[0])}
        className="hidden"
        data-testid="photo-input"
      />
      {!photoPath && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid="photo-pick-btn"
          className="w-full border border-dashed border-[var(--rule)] hover:border-[var(--wax)] transition-colors py-12 px-6 text-[var(--ink-soft)] hover:text-[var(--wax)] flex flex-col items-center gap-3"
        >
          {uploading ? <Loader2 className="animate-spin" /> : <Camera size={26} />}
          <span className="italic">{uploading ? t("form.uploading") : t("form.photo_drop")}</span>
        </button>
      )}
      {photoPath && (
        <div className="flex flex-col items-center gap-5 py-4">
          <div className="polaroid -rotate-2" style={{ width: 280 }}>
            <img src={fileUrl(photoPath)} alt="your moment" data-testid="photo-preview" />
            <div className="mt-3 text-center font-en italic text-[13px] text-[var(--ink-soft)]">
              {new Date().toLocaleDateString()}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onClear();
              inputRef.current.value = "";
              inputRef.current.click();
            }}
            data-testid="photo-replace-btn"
            className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)] transition-colors"
          >
            {t("form.photo_replace")}
          </button>
        </div>
      )}
    </div>
  );
};

const CheckoutSummary = ({ lang, price, priceFmt, deliveryDate, promoActive, promoKind, promoDiscountPercent }) => {
  const { t } = useTranslation();

  // Slot-capped free path: strikethrough base + FREE label.
  const isFreeSlot = promoActive && promoKind === "slot_capped_free";
  // Percent-off community path: strikethrough base + discounted price.
  const isPercentOff = promoActive && promoKind === "checkout_percent_off";
  const discountedPrice = isPercentOff
    ? Math.round(price * (1 - (Number(promoDiscountPercent) || 0) / 100) * 100) / 100
    : null;
  const discountedFmt = discountedPrice != null
    ? CURRENCY_FORMAT[lang in CURRENCY_FORMAT ? lang : "en"](discountedPrice)
    : null;

  return (
    <div className="border-t border-b border-[var(--rule)] py-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--ink-soft)]">
          {t("form.checkout_summary")}
        </div>
        {isFreeSlot ? (
          <div className="mt-1 flex items-baseline gap-3" data-testid="price-display">
            <span className="font-display text-[var(--ink-mute)] text-xl line-through decoration-[var(--ink-mute)]/60">
              {priceFmt}
            </span>
            <span className="font-display text-3xl text-[var(--wax)]">
              {t("promo.free_label")}
            </span>
          </div>
        ) : isPercentOff ? (
          <div className="mt-1 flex items-baseline gap-3 flex-wrap" data-testid="price-display">
            <span className="font-display text-[var(--ink-mute)] text-xl line-through decoration-[var(--ink-mute)]/60">
              {priceFmt}
            </span>
            <span className="font-display text-3xl text-[var(--wax)]" data-testid="price-discounted">
              {discountedFmt}
            </span>
            <span className="text-[11px] uppercase tracking-[0.28em] text-[var(--wax)]/80" data-testid="price-discount-badge">
              −{promoDiscountPercent}%
            </span>
          </div>
        ) : (
          <div className="font-display text-3xl mt-1" data-testid="price-display">{priceFmt}</div>
        )}
      </div>
      <div className="text-[var(--ink-soft)] text-[15px] italic">
        {t("form.delivery_date")}{" "}
        <span className="text-[var(--ink)] not-italic" data-testid="delivery-date">{deliveryDate}</span>
      </div>
    </div>
  );
};

const PromoBlock = ({
  promoCode, promoRemaining, promoKind, promoDiscountPercent,
  promoInputOpen, promoDraft, promoChecking,
  onOpenInput, onDraftChange, onApply, onCloseInput, onRemove,
}) => {
  const { t } = useTranslation();

  // Applied state — small parchment badge
  if (promoCode) {
    const isPercentOff = promoKind === "checkout_percent_off";
    return (
      <div
        data-testid="promo-applied-badge"
        className="flex items-center justify-between gap-4 border border-[var(--wax)]/40 bg-[var(--wax)]/[0.04] px-5 py-4"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Sparkles size={14} className="text-[var(--wax)] shrink-0" />
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[var(--wax)] truncate">
              {t("promo.applied_prefix")} {promoCode}
            </div>
            {isPercentOff ? (
              <div className="text-[11px] italic text-[var(--ink-soft)] mt-1" data-testid="promo-discount-subtitle">
                −{promoDiscountPercent}% off
              </div>
            ) : typeof promoRemaining === "number" && promoRemaining > 0 && (
              <div className="text-[11px] italic text-[var(--ink-soft)] mt-1">
                {t("promo.spots_remaining", { n: promoRemaining })}
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          data-testid="promo-remove-btn"
          className="text-[11px] uppercase tracking-[0.2em] text-[var(--ink-soft)] hover:text-[var(--wax)] inline-flex items-center gap-1 shrink-0"
        >
          <XIcon size={12} /> {t("promo.remove")}
        </button>
      </div>
    );
  }

  // Input state — collapsible one-liner
  if (promoInputOpen) {
    return (
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={promoDraft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onApply(); } }}
          placeholder={t("promo.code_placeholder")}
          data-testid="promo-code-input"
          autoFocus
          maxLength={64}
          className="flex-1 focus-line text-[16px] py-2 text-[var(--ink)] uppercase tracking-[0.15em] placeholder:text-[var(--ink-mute)] placeholder:normal-case placeholder:italic placeholder:tracking-normal"
        />
        <button
          type="button"
          onClick={onApply}
          disabled={promoChecking || !promoDraft.trim()}
          data-testid="promo-apply-btn"
          className="text-[11px] uppercase tracking-[0.22em] text-[var(--wax)] hover:text-[var(--ink)] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
        >
          {promoChecking ? <Loader2 size={12} className="animate-spin" /> : null}
          {promoChecking ? t("promo.checking") : t("promo.apply")}
        </button>
        <button
          type="button"
          onClick={onCloseInput}
          className="text-[var(--ink-soft)] hover:text-[var(--wax)]"
          data-testid="promo-close-btn"
          aria-label="close"
        >
          <XIcon size={14} />
        </button>
      </div>
    );
  }

  // Idle state — subtle inline link
  return (
    <div className="text-right -mt-2">
      <button
        type="button"
        onClick={onOpenInput}
        data-testid="promo-open-btn"
        className="text-[11px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)] italic normal-case tracking-normal"
      >
        {t("promo.have_code")}
      </button>
    </div>
  );
};
